import React from "react";
import {
    Dialog,
    DialogTrigger,
    Modal,
    ModalOverlay,
} from "react-aria-components";
import { t } from "ttag";

import {
    concatClassNames,
    getStyleVariantClass,
} from "@thelabnyc/thelabui/src/utils/styles";

import { Themed } from "../../styles/themes";
import type { Theme } from "../../styles/themes";
import { Clickable } from "../Clickables";
import { Svg } from "../Svg";

import styles from "./CenteredModal.module.scss";

const overlayVariants = {
    dark: styles.overlayDark,
    medium: styles.overlayMedium,
    light: styles.overlayLight,
};

type OverlayWeight = keyof typeof overlayVariants;

interface DialogProps {
    theme: Theme;
    panelClassName?: string;
    overlay?: OverlayWeight;
    labelledBy?: string;
    label?: string;
    children: React.ReactNode;
}

const CenteredDialog = ({
    theme,
    panelClassName,
    overlay = "dark",
    labelledBy,
    label,
    children,
}: DialogProps) => (
    <Dialog
        className={styles.dialog}
        aria-labelledby={labelledBy}
        aria-label={label}
    >
        {({ close }) => (
            <>
                <Clickable
                    className={concatClassNames([
                        styles.overlay,
                        getStyleVariantClass(overlayVariants, overlay),
                    ])}
                    onPress={close}
                    aria-hidden="true"
                    tabIndex={-1}
                />
                <Themed
                    theme={theme}
                    tag="div"
                    className={concatClassNames([
                        styles.content,
                        panelClassName,
                    ])}
                >
                    <Clickable className={styles.close} onPress={close}>
                        <Svg name="x" visuallyHiddenText={t`Close`} />
                    </Clickable>
                    {children}
                </Themed>
            </>
        )}
    </Dialog>
);

/** Centered, grid-aligned modal that manages its own open state via a trigger. */
export const CenteredModal = ({
    trigger,
    ...dialog
}: { trigger: React.ReactNode } & DialogProps) => (
    <DialogTrigger>
        {trigger}
        <ModalOverlay isDismissable>
            <Modal className={styles.modal}>
                <CenteredDialog {...dialog} />
            </Modal>
        </ModalOverlay>
    </DialogTrigger>
);

/** Same modal, but open state is owned by the parent (no trigger element). */
export const CenteredModalControlled = ({
    isOpen,
    onOpenChange,
    ...dialog
}: {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
} & DialogProps) => (
    <ModalOverlay isDismissable isOpen={isOpen} onOpenChange={onOpenChange}>
        <Modal className={styles.modal}>
            <CenteredDialog {...dialog} />
        </Modal>
    </ModalOverlay>
);
